import React from "react";
import OrgPortalSectionTitle from "./OrgPortalSectionTitle";
import { Divider } from "@mui/material";
import useTranslation from "../../../i18n/useTranslation";

export default function OrgPortalUpcomingGamesSection({ upcoming, theme }) {
    const {
        surfaceColor,
        cardBorder,
        textOnSurface,
        accentColor,
        primaryColor,
        textOnBackground, // still used for neutral/no-play badge
    } = theme;
    const {t} = useTranslation();

    // Helper: nice date/time string using event timezone if available
    const formatDateTime = (iso, timezone) => {
        if (!iso) return "";
        const d = new Date(iso);
        const optsDate = { weekday: "short", month: "short", day: "numeric" };
        const optsTime = { hour: "numeric", minute: "2-digit" };

        const datePart = d.toLocaleDateString(undefined, {
            ...optsDate,
            ...(timezone ? { timeZone: timezone } : {}),
        });
        const timePart = d.toLocaleTimeString(undefined, {
            ...optsTime,
            ...(timezone ? { timeZone: timezone } : {}),
        });

        let tzLabel = "";
        if (timezone) {
            const parts = String(timezone).split("/");
            if (parts.length === 2) {
                tzLabel = parts[1].replace("_", " ");
            }
        }

        return tzLabel ? `${datePart} · ${timePart} (${tzLabel})` : `${datePart} · ${timePart}`;
    };

    // Helper: competition context (division / season / stage / group)
    const formatContext = (g) => {
        const divisionName = g.season?.division?.name;
        const seasonName = g.season?.name;
        const stageName = g.stage?.name;
        const groupName =
            g.game?.group?.name || (g.stage?.groups && g.stage.groups[0]?.name);

        const parts = [];
        if (divisionName) parts.push(divisionName);
        if (seasonName) parts.push(seasonName);
        if (stageName) parts.push(stageName);
        if (groupName) parts.push(`Group ${groupName}`);

        return parts.join(" • ");
    };

    // Helper: team logo avatar (with initials fallback)
    const TeamAvatar = ({ name, logoUrl }) => {
        const initials = React.useMemo(() => {
            if (!name) return "?";
            return name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase())
                .join("");
        }, [name]);

        const size = 32;

        if (logoUrl) {
            return (
                <div
                    style={{
                        width: size,
                        height: size,
                        borderRadius: "50%",
                        overflow: "hidden",
                        flexShrink: 0,
                        border: `1px solid ${primaryColor}55`,
                        // 🔹 use surface tinted instead of hard black
                        backgroundColor: surfaceColor,
                    }}
                >
                    <img
                        src={logoUrl}
                        alt={name}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />
                </div>
            );
        }

        return (
            <div
                style={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: `${accentColor}22`,
                    border: `1px solid ${accentColor}66`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: accentColor,
                }}
            >
                {initials}
            </div>
        );
    };

    // Helper: responsibles string
    const formatResponsibles = (responsibles) => {
        if (!responsibles || !responsibles.length) return "";
        // support both [{fullName}] and ["Name"]
        const names = responsibles.map((r) =>
            typeof r === "string" ? r : r.fullName || ""
        );
        const filtered = names.filter(Boolean);
        if (!filtered.length) return "";
        return `Responsibles: ${filtered.join(", ")}`;
    };

    return (
        <section style={{ marginBottom: 50 }}>
            <OrgPortalSectionTitle title={t('home.upcomingGames.title')} theme={theme} />

            {/* 🔹 Section divider uses primaryColor as in Divisions */}
            <Divider style={{ marginBottom: 20 }} color={primaryColor} />

            {upcoming.length === 0 ? (
                <div
                    style={{
                        backgroundColor: surfaceColor,
                        borderRadius: 10,
                        padding: 16,
                        border: `1px solid ${cardBorder}`,
                        color: textOnSurface,
                        fontSize: 14,
                        opacity: 0.8,
                    }}
                >
                    {t('home.upcomingGames.noItems')}
                </div>
            ) : (
                upcoming.map((g) => {
                    const game = g.game || {};
                    const isNoPlay = game.noPlay;

                    const homeName = game.homeTeam?.name || game.homeTeamAlias || "Home";
                    const homeLogo = game.homeTeam?.logoUrl || null;
                    const awayName = game.visitorTeam?.name || game.visitorTeamAlias || "Away";
                    const awayLogo = game.visitorTeam?.logoUrl || null;


                    const dateStr = formatDateTime(g.date, g.timezone);
                    const context = formatContext(g);
                    const fieldName = g.field?.name;
                    const responsiblesText = formatResponsibles(g.responsibles);

                    // Status badge styling (accent vs neutral)
                    const statusText = isNoPlay ? "No play" : g.status || "Scheduled";
                    const statusBg = isNoPlay ? `${textOnBackground}22` : `${accentColor}22`;
                    const statusBorder = isNoPlay ? `${textOnBackground}55` : `${accentColor}55`;
                    const statusColor = isNoPlay ? textOnBackground : accentColor;

                    return (
                        <article
                            key={g.id}
                            style={{
                                // 🔹 Card surface pattern
                                backgroundColor: surfaceColor,
                                padding: 14,
                                borderRadius: 12,
                                marginBottom: 12,
                                border: `1px solid ${cardBorder}`,
                                color: textOnSurface,
                                fontSize: 14,
                                boxShadow: "0 8px 26px rgba(0,0,0,0.25)",
                            }}
                        >
                            {/* Top row: context + status */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 12,
                                    marginBottom: 8,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 14,
                                        opacity: 0.85,
                                        color: textOnSurface,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontWeight: 900,
                                    }}
                                    title={context || g.title}
                                >
                                    {context || g.title || "League game"}
                                </div>

                                <div
                                    style={{
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border: `1px solid ${statusBorder}`,
                                        backgroundColor: statusBg,
                                        color: statusColor,
                                        fontSize: 11,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {statusText}
                                </div>
                            </div>

                            {/* Middle row: teams with logos */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 16,
                                    marginBottom: 8,
                                }}
                            >
                                {/* Away */}
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        minWidth: 0,
                                    }}
                                >
                                    <TeamAvatar name={awayName} logoUrl={awayLogo} />
                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                opacity: 0.7,
                                                color: textOnSurface,
                                                marginBottom: 2,
                                            }}
                                        >
                                            Away
                                        </div>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 15,
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {awayName}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        flexShrink: 0,
                                        fontSize: 18,
                                        opacity: 0.8,
                                        textAlign: "center",
                                        minWidth: 40,
                                    }}
                                >
                                    <div style={{ fontWeight: 600 }}>@</div>
                                </div>

                                {/* Home */}
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        minWidth: 0,
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <div style={{ minWidth: 0, textAlign: "right" }}>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                opacity: 0.7,
                                                color: textOnSurface,
                                                marginBottom: 2,
                                            }}
                                        >
                                            Home
                                        </div>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 15,
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {homeName}
                                        </div>
                                    </div>
                                    <TeamAvatar name={homeName} logoUrl={homeLogo} />
                                </div>
                            </div>

                            {/* Bottom rows: date/field + responsibles */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    marginTop: 4,
                                    fontSize: 12,
                                    opacity: 0.9,
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    {dateStr && <span>{dateStr}</span>}
                                    {fieldName && (
                                        <span style={{ opacity: 0.85 }}>
                                            Field: <strong>{fieldName}</strong>
                                        </span>
                                    )}
                                </div>

                                {responsiblesText && (
                                    <div
                                        style={{
                                            marginLeft: "auto",
                                            fontSize: 11,
                                            opacity: 0.85,
                                            textAlign: "right",
                                            maxWidth: 320,
                                        }}
                                        title={responsiblesText}
                                    >
                                        {responsiblesText}
                                    </div>
                                )}
                            </div>
                        </article>
                    );
                })
            )}
        </section>
    );
}